import React, { useState, useEffect, useRef } from 'react';
import * as mui from '@mui/material';
import { styled } from '@mui/material/styles';
import Select from 'react-select';
import LinkIcon from '@mui/icons-material/Link';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import SecurityIcon from '@mui/icons-material/Security';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import InsightsIcon from '@mui/icons-material/Insights';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import auditService from '../../../services/AuditService';

// Styled components
const StyledSection = styled(mui.Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
  boxShadow: 'none',
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`
}));

const SectionHeader = styled(mui.Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'rgba(25, 118, 210, 0.02)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}));

const SectionContent = styled(mui.Box)(({ theme }) => ({
  padding: theme.spacing(3)
}));

const ControlListContainer = styled(mui.Box)(({ theme }) => ({
  maxHeight: '350px',
  overflow: 'auto',
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper
}));

const ControlItem = styled(mui.ListItem)(({ theme, mapped }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1.5),
  backgroundColor: mapped ? 'rgba(25, 118, 210, 0.02)' : theme.palette.background.paper,
  border: `1px solid ${mapped ? 'rgba(25, 118, 210, 0.15)' : theme.palette.divider}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
  },
  '&:last-child': {
    marginBottom: 0
  }
}));

const SearchBox = styled(mui.Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 1.5),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s',
  '&:focus-within': {
    borderColor: 'rgba(25, 118, 210, 0.6)',
    boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.2)'
  }
}));

const RiskBadge = styled(mui.Chip)(({ level, theme }) => {
  const colors = {
    High: {
      bg: 'rgba(211, 47, 47, 0.08)',
      color: 'rgba(211, 47, 47, 0.9)'
    },
    Medium: {
      bg: 'rgba(237, 108, 2, 0.08)',
      color: 'rgba(237, 108, 2, 0.9)'
    },
    Low: {
      bg: 'rgba(46, 125, 50, 0.08)',
      color: 'rgba(46, 125, 50, 0.9)'
    }
  };
  
  const colorSet = colors[level] || { bg: theme.palette.grey[100], color: theme.palette.text.secondary };
  
  return {
    backgroundColor: colorSet.bg,
    color: colorSet.color,
    fontWeight: 500,
    '.MuiChip-label': {
      padding: '0 10px'
    }
  };
});

const AutoSaveIndicator = styled(mui.Box)(({ theme, saving }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: saving ? 'rgba(25, 118, 210, 0.06)' : 'transparent',
  transition: 'all 0.3s',
  marginLeft: 'auto'
}));

// Add or update the selectStyles constant to make the dropdown more visible
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused 
      ? 'rgba(25, 118, 210, 0.5)' 
      : 'rgba(25, 118, 210, 0.2)',
    borderRadius: '4px',
    boxShadow: state.isFocused 
      ? '0 0 0 2px rgba(25, 118, 210, 0.2)' 
      : 'none',
    '&:hover': {
      borderColor: 'rgba(25, 118, 210, 0.5)'
    },
    minHeight: '40px'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? 'rgba(25, 118, 210, 0.8)' 
      : state.isFocused 
        ? 'rgba(25, 118, 210, 0.1)' 
        : null,
    color: state.isSelected 
      ? 'white' 
      : 'inherit',
    padding: '8px 12px'
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    borderRadius: '4px'
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = 'opacity 300ms';
    
    // Get color based on value
    let color = 'inherit';
    if (state.data.value === 'High') {
      color = 'rgba(211, 47, 47, 0.9)';
    } else if (state.data.value === 'Medium') {
      color = 'rgba(237, 108, 2, 0.9)';
    } else if (state.data.value === 'Low') {
      color = 'rgba(46, 125, 50, 0.9)';
    }
    
    return { ...provided, opacity, transition, color, fontWeight: 600 };
  }
};

/**
 * ConsolidatedRiskDialog Component
 * 
 * A dialog for managing risk mappings and control assignments
 * 
 * @param {Object} props - Props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Object} props.risk - Risk object with properties:
 *   - id: string - Risk ID
 *   - RISK_ID: string - Risk ID display value
 *   - RISK_NAME: string - Risk name
 *   - RISK_TYPE: string - Risk type
 *   - RISK_DESCRIPTION: string - Risk description
 *   - RATING: string - (Optional) Risk rating value ('High', 'Medium', 'Low', or '')
 * @param {string} props.appId - Application ID
 * @param {string} props.companyId - Company ID
 * @param {Array} props.controls - Array of control objects
 * @param {Function} props.onClose - Function to call when dialog is closed
 * @param {Function} props.onSaveSuccess - Function to call when save is successful
 */
const ConsolidatedRiskDialog = ({ 
  open, 
  onClose, 
  risk, 
  appId, 
  companyId, 
  controls,
  onSaveSuccess
}) => {
  // State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMessage, setSavingMessage] = useState('');
  const [riskRating, setRiskRating] = useState({
    RATING: '',
    RATING_RATIONALE: ''
  });
  const [mappedControls, setMappedControls] = useState([]);
  const [availableControls, setAvailableControls] = useState([]);
  const [controlSearchText, setControlSearchText] = useState('');
  const saveTimeout = useRef(null);
  const [mappingControl, setMappingControl] = useState(null);

  // Risk rating options
  const riskLevelOption = [
    { value: 'High', label: 'High' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Low', label: 'Low' },
  ];

  // Fetch already mapped controls and risk rating on dialog open
  useEffect(() => {
    if (open && risk) {
      console.log("Dialog opened with risk:", risk);
      fetchRiskData();
      prepareControlsData();
    }
    
    return () => {
      // Clean up timeout on unmount
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
    };
  }, [open, risk, controls]);
  
  // Additional effect to log and handle risk data
  useEffect(() => {
    if (open && risk) {
      // Log received risk data for debugging
      console.log("Risk data received by dialog:", risk);
      
      // Always set the initial rating from the prop, even if it's 'Not Set'
      // This ensures consistency with what's displayed in the table
      console.log("Setting initial risk rating from risk prop:", risk.RATING);
      setRiskRating(prev => ({
        ...prev,
        RATING: risk.RATING || prev.RATING
      }));
    }
  }, [open, risk]);

  // Log the actual data received for debugging
  useEffect(() => {
    if (controls && controls.length > 0) {
      console.log("Controls passed to dialog:", controls[0]);
    }
  }, [controls]);

  const fetchRiskData = async () => {
    if (!risk || !risk.id) {
      console.error("No valid risk ID provided to dialog");
      return;
    }
    
    console.log("Fetching risk data for ID:", risk.id);
    setLoading(true);
    
    try {
      // First ensure the risk is mapped if not already
      let existingMapping = null;
      try {
        existingMapping = await auditService.fetchRiskMappingbyId(risk.id);
        console.log("Existing risk mapping found:", existingMapping);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          // Create risk mapping if not found
          console.log("No existing mapping found, creating new mapping for risk ID:", risk.id);
          const mapRiskData = {
            RISK_ID: risk.id,
            APP_NAME: appId,
            COMPANY_ID: companyId,
            // Include any rating that was passed to us
            RATING: risk.RATING || 'Not Set'
          };
          existingMapping = await auditService.createRiskMapping(mapRiskData);
          console.log("New mapping created:", existingMapping);
        } else {
          console.error("Error checking existing mapping:", error);
        }
      }
      
      // Now fetch the mapped controls
      const mappedControlsData = await auditService.fetchMappedControls(appId, risk.id, companyId);
      console.log("Mapped Controls Data:", mappedControlsData);
      
      if (mappedControlsData && mappedControlsData.length > 0) {
        // Set the mapped controls, but ensure they have proper CONTROL_ID values
        let mappedControlDetails = mappedControlsData[0].CONTROL_DETAILS || [];
        
        // Debug log the control details to see their structure
        console.log("Original mapped control details:", JSON.stringify(mappedControlDetails));
        
        // Find any controls that are missing CONTROL_ID values
        const missingControlIds = mappedControlDetails.filter(control => !control.CONTROL_ID).map(c => c.id);
        if (missingControlIds.length > 0) {
          console.log("Found controls missing CONTROL_ID:", missingControlIds);
          
          try {
            // Fetch the full controls library to get the missing CONTROL_ID values
            const fullControlsList = await auditService.fetchControls();
            console.log("Full controls list sample:", fullControlsList.slice(0, 2));
            
            // Map the control details to include CONTROL_ID from the full controls list
            mappedControlDetails = mappedControlDetails.map(control => {
              // If control already has CONTROL_ID, just return it
              if (control.CONTROL_ID) {
                return control;
              }
              
              // Try to find the control in the full list by id
              const fullControl = fullControlsList.find(c => c.id === control.id);
              if (fullControl && fullControl.CONTROL_ID) {
                console.log(`Found CONTROL_ID "${fullControl.CONTROL_ID}" for control with id ${control.id}`);
                return {
                  ...control,
                  CONTROL_ID: fullControl.CONTROL_ID
                };
              }
              
              return control;
            });
            
            console.log("Updated mapped control details:", mappedControlDetails.map(c => ({ id: c.id, CONTROL_ID: c.CONTROL_ID })));
          } catch (error) {
            console.error("Error fetching full controls list:", error);
          }
        }
        
        setMappedControls(mappedControlDetails);
        
        // Determine the best rating to use
        // Priority: 1. API data 2. risk prop 3. default
        const apiRating = mappedControlsData[0].RATING;
        const passedRating = risk.RATING;
        
        // Log the various sources of rating
        console.log("Rating sources - API:", apiRating, "Passed:", passedRating);
        
        // Use the best available rating
        // If we have both API and passed rating, prefer the passed rating if it's not 'Not Set'
        // This ensures the most recent user selection is preserved
        let bestRating;
        if (passedRating && passedRating !== 'Not Set') {
          // If there's a valid passed rating, use it (prioritize user's latest choice)
          bestRating = passedRating;
          console.log("Using passed rating (priority 1):", bestRating);
        } else if (apiRating) {
          // If no passed rating but there is an API rating, use it
          bestRating = apiRating;
          console.log("Using API rating (priority 2):", bestRating);
        } else {
          // Fallback to empty if neither is available
          bestRating = '';
          console.log("No rating found, using default empty string");
        }
        
        const rationale = mappedControlsData[0].RATING_RATIONALE || '';
        
        console.log("Using final rating:", bestRating);
        
        setRiskRating({
          RATING: bestRating,
          RATING_RATIONALE: rationale
        });
      } else {
        setMappedControls([]);
        
        // If no mapped data but risk has a rating, use that
        setRiskRating({
          RATING: risk.RATING || '',
          RATING_RATIONALE: ''
        });
      }
    } catch (error) {
      console.error('Error fetching risk data:', error);
      setMappedControls([]);
      
      // Even on error, try to use any rating that was passed in
      if (risk.RATING) {
        setRiskRating(prev => ({
          ...prev,
          RATING: risk.RATING
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const prepareControlsData = () => {
    if (controls && controls.length > 0) {
      // Debug log: Check if controls have CONTROL_ID values
      const missingIds = controls.filter(control => !control.CONTROL_ID).length;
      console.log(`Found ${missingIds} controls missing CONTROL_ID out of ${controls.length} total controls`);
      
      if (missingIds > 0) {
        console.log("Sample of controls with missing IDs:", 
          controls.filter(control => !control.CONTROL_ID).slice(0, 3));
        
        // Fix available controls with missing CONTROL_ID values
        const enhancedControls = controls.map(control => {
          if (control.CONTROL_ID) {
            return control;
          }
          
          // If CONTROL_ID is missing but we have an id, use it as the CONTROL_ID
          if (control.id) {
            console.log(`Creating CONTROL_ID from id for control: ${control.id}`);
            return {
              ...control,
              CONTROL_ID: control.controlID || `CTL-${control.id.substring(0, 8)}`
            };
          }
          
          return control;
        });
        
        console.log("Sample of fixed controls:", 
          enhancedControls.filter(c => c.CONTROL_ID).slice(0, 3));
        
        setAvailableControls(enhancedControls);
      } else {
        setAvailableControls(controls);
      }
    }
  };

  // Show auto-save indicators
  const showSavingIndicator = (message) => {
    setSaving(true);
    setSavingMessage(message);
    
    // Hide the indicator after 1.5 seconds
    setTimeout(() => {
      setSaving(false);
      setSavingMessage('');
    }, 1500);
  };

  // Handle risk rating change
  const handleRatingChange = (selectedOption) => {
    const newRating = selectedOption?.value || '';
    
    setRiskRating(prev => ({
      ...prev,
      RATING: newRating
    }));
    
    // Auto-save on change
    const updatedData = {
      ...riskRating,
      RATING: newRating,
      RISK_ID: risk.id,
      APP_NAME: appId,
      COMPANY_ID: companyId
    };
    
    showSavingIndicator('Saving risk rating...');
    saveRiskRating(updatedData);
  };

  // Handle rationale text change
  const handleRationaleChange = (e) => {
    const newRationale = e.target.value;
    
    setRiskRating(prev => ({
      ...prev,
      RATING_RATIONALE: newRationale
    }));
    
    // Clear any existing timeout
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }
    
    // Set a new timeout for auto-saving
    saveTimeout.current = setTimeout(() => {
      const updatedData = {
        ...riskRating,
        RATING_RATIONALE: newRationale,
        RISK_ID: risk.id,
        APP_NAME: appId,
        COMPANY_ID: companyId
      };
      
      showSavingIndicator('Saving rationale...');
      saveRiskRating(updatedData);
    }, 800); // Debounce for 800ms
  };

  // Handle rationale blur - save immediately when user stops typing
  const handleRationaleBlur = () => {
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }
    
    const updatedData = {
      ...riskRating,
      RISK_ID: risk.id,
      APP_NAME: appId,
      COMPANY_ID: companyId
    };
    
    showSavingIndicator('Saving rationale...');
    saveRiskRating(updatedData);
  };

  // Save risk rating
  const saveRiskRating = async (updatedData) => {
    if (!risk || !risk.id) return;

    try {
      await auditService.updateRiskMappingbyMapID(appId, risk.id, companyId, updatedData);
      
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      
      return true;
    } catch (error) {
      console.error('Error saving risk rating:', error);
      return false;
    }
  };

  // Map a control to the risk
  const handleMapControl = async (control) => {
    if (!risk || !risk.id) return;
    
    console.log("Mapping control:", control);
    
    // Set loading for this specific control
    setMappingControl(control.controlID || control.id);
    setLoading(true);
    showSavingIndicator('Mapping control...');
    
    // Get the correct control ID, handling different possible property names
    const controlId = control.controlID || control.id;
    
    if (!controlId) {
      console.error("Cannot map control: No control ID found", control);
      setLoading(false);
      setMappingControl(null);
      return;
    }
    
    const updatedData = {
      CONTROL_ID: controlId,
      RISK_ID: risk.id,
      APP_NAME: appId,
      COMPANY_ID: companyId
    };
    
    console.log("Sending map data:", updatedData);

    try {
      const result = await auditService.updateRiskMappingbyMapID(appId, risk.id, companyId, updatedData);
      console.log("Control mapping result:", result);
      await fetchRiskData(); // Refresh data
      
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      console.error('Error mapping control:', error);
    } finally {
      setLoading(false);
      setMappingControl(null);
    }
  };

  // Unmap a control from the risk
  const handleUnmapControl = async (control) => {
    if (!risk || !risk.id) return;
    
    console.log("Unmapping control:", control);
    
    // Set loading for this specific control
    setMappingControl(control.controlID || control.id);
    setLoading(true);
    showSavingIndicator('Removing control mapping...');
    
    // Get the correct control ID, handling different possible property names
    const controlId = control.controlID || control.id;
    
    if (!controlId) {
      console.error("Cannot unmap control: No control ID found", control);
      setLoading(false);
      setMappingControl(null);
      return;
    }
    
    const updatedData = {
      ACTION: 'UNMAP',
      CONTROL_ID: controlId,
      RISK_ID: risk.id,
      APP_NAME: appId,
      COMPANY_ID: companyId
    };
    
    console.log("Sending unmap data:", updatedData);

    try {
      const result = await auditService.updateRiskMappingbyMapID(appId, risk.id, companyId, updatedData);
      console.log("Control unmapping result:", result);
      await fetchRiskData(); // Refresh data
      
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      console.error('Error unmapping control:', error);
    } finally {
      setLoading(false);
      setMappingControl(null);
    }
  };

  // Filter controls by search text
  const filterControls = (controls) => {
    if (!controlSearchText) return controls;
    
    return controls.filter(control => 
      (control.controlID && control.controlID.toLowerCase().includes(controlSearchText.toLowerCase())) ||
      (control.CONTROL_NAME && control.CONTROL_NAME.toLowerCase().includes(controlSearchText.toLowerCase())) ||
      (control.CONTROL_DESCRIPTION && control.CONTROL_DESCRIPTION.toLowerCase().includes(controlSearchText.toLowerCase())) ||
      (control.CONTROL_TITLE && control.CONTROL_TITLE.toLowerCase().includes(controlSearchText.toLowerCase()))
    );
  };

  // Filter out already mapped controls
  const getUnmappedControls = () => {
    console.log("Available Controls:", availableControls);
    console.log("Mapped Controls:", mappedControls);
    
    if (!availableControls || !mappedControls) {
      return availableControls || [];
    }
    
    return availableControls.filter(control => {
      // Enhanced logging to debug control mapping issues
      const controlIdToCheck = control.controlID || control.id;
      const isMapped = mappedControls.some(mapped => 
        (mapped.controlID || mapped.id) === controlIdToCheck
      );
      
      if (controlIdToCheck) {
        console.log(`Control ${controlIdToCheck} is${isMapped ? '' : ' not'} mapped`);
      }
      
      return !isMapped;
    });
  };
  
  // Helper function to get control title for display
  const getControlTitle = (control) => {
    // Add logging to see what control object looks like
    console.log("Getting title for control:", control);
    
    // Make sure we have a control object
    if (!control) {
      console.error("Received null or undefined control object");
      return "Unknown Control";
    }
    
    // Check for various possible control title fields
    if (control.CONTROL_TITLE) return control.CONTROL_TITLE;
    if (control.CONTROL_NAME) return control.CONTROL_NAME;
    if (control.controlTitle) return control.controlTitle;
    if (control.controlName) return control.controlName;
    if (control.title) return control.title;
    if (control.name) return control.name;
    
    // If no title found, return the ID or a fallback
    const displayId = control.CONTROL_ID || control.controlID || control.id;
    if (displayId) {
      console.log(`Using ID as title for control: ${displayId}`);
      return `Control ${displayId.substring(0, 8)}`;
    }
    
    console.warn("No title or ID found for control:", control);
    return 'Unknown Control';
  };

  // Helper function to render a control in the list
  const renderControl = (control, index) => {
    const controlTitle = getControlTitle(control);
    const controlId = control.CONTROL_ID || control.controlID || control.id || '';
    const controlDescription = control.CONTROL_DESCRIPTION || control.controlDescription || control.description || '';
    
    console.log(`Rendering control ${index}:`, { 
      controlId, 
      controlTitle, 
      controlDescription,
      originalControl: control 
    });
    
    return (
      <mui.Box sx={{ width: '100%' }}>
        <mui.ListItemText
          primary={
            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
              {controlId && (
                <mui.Chip 
                  label={controlId} 
                  size="small" 
                  variant="outlined" 
                  sx={{ 
                    mr: 1,
                    fontSize: '0.75rem',
                    height: 20,
                    borderColor: 'rgba(25, 118, 210, 0.3)',
                  }}
                />
              )}
              <mui.Typography variant="body2" fontWeight={500}>
                {controlTitle}
              </mui.Typography>
            </mui.Box>
          }
          secondary={
            controlDescription && (
              <mui.Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  display: 'block', 
                  mt: 0.5,
                  pr: 4, // Add right padding to prevent overlap with the action icon
                  wordBreak: 'break-word'
                }}
              >
                {controlDescription}
              </mui.Typography>
            )
          }
        />
      </mui.Box>
    );
  };

  return (
    <mui.Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2, 
          minHeight: '80vh',
          maxHeight: '90vh',
          overflow: 'visible',
          '& .MuiDialogContent-root': {
            overflowY: 'auto'
          }
        }
      }}
    >
      {/* Dialog Header */}
      <mui.DialogTitle sx={{ 
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        backgroundColor: 'background.paper',
        zIndex: 1
      }}>
        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ErrorOutlineIcon sx={{ mr: 1, color: 'rgba(25, 118, 210, 0.7)' }} />
          <mui.Typography variant="h6" component="span" fontWeight={500}>
            Risk Management
          </mui.Typography>
        </mui.Box>
        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
          {saving && (
            <AutoSaveIndicator saving={saving}>
              <mui.CircularProgress size={16} thickness={4} sx={{ mr: 1 }} />
              <mui.Typography variant="caption" color="text.secondary">
                {savingMessage}
              </mui.Typography>
            </AutoSaveIndicator>
          )}
          {riskRating.RATING && (
            <RiskBadge 
              level={riskRating.RATING} 
              label={`${riskRating.RATING} Risk`}
              size="small"
            />
          )}
        </mui.Box>
      </mui.DialogTitle>
      
      {/* Dialog Content */}
      <mui.DialogContent sx={{ 
        p: 3, 
        pb: 4,
        overflowY: 'auto',
        position: 'relative'
      }}>
        {loading && (
          <mui.Backdrop
            sx={{ 
              color: 'rgba(25, 118, 210, 0.7)',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: (theme) => theme.zIndex.drawer + 1, 
              position: 'absolute',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            open={loading}
          >
            <mui.CircularProgress color="inherit" size={40} thickness={4} />
            <mui.Typography 
              variant="body2" 
              sx={{ 
                mt: 2, 
                fontWeight: 500,
                color: 'primary.main',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                px: 2,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              Loading risk data...
            </mui.Typography>
          </mui.Backdrop>
        )}
        
        {/* Risk Description Section */}
        {risk && risk.RISK_DESCRIPTION && (
          <StyledSection elevation={0} sx={{ mt: 0 }}>
            <SectionHeader>
              <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InfoIcon sx={{ mr: 1, color: 'rgba(25, 118, 210, 0.7)' }} />
                <mui.Typography variant="subtitle1" fontWeight={500}>
                  Risk Description
                </mui.Typography>
              </mui.Box>
            </SectionHeader>
            <SectionContent sx={{ py: 2 }}>
              {risk && risk.RISK_ID && risk.RISK_NAME && (
                <mui.Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <mui.Chip 
                    label={risk.RISK_ID} 
                    size="small" 
                    variant="outlined" 
                    sx={{ mr: 1, borderColor: 'rgba(25, 118, 210, 0.3)' }}
                  />
                  <mui.Typography variant="subtitle1" fontWeight={500}>
                    {risk.RISK_NAME}
                  </mui.Typography>
                </mui.Box>
              )}
              
              <mui.Typography 
                variant="body1" 
                color="text.primary" 
                sx={{ 
                  lineHeight: 1.7,
                  backgroundColor: 'rgba(25, 118, 210, 0.02)',
                  p: 2,
                  borderRadius: 1,
                  border: `1px solid rgba(25, 118, 210, 0.1)`,
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  overflow: 'auto',
                  maxHeight: '200px'
                }}
              >
                {risk.RISK_DESCRIPTION}
              </mui.Typography>
            </SectionContent>
          </StyledSection>
        )}
        
        {/* Risk Assessment Section */}
        <StyledSection elevation={0}>
          <SectionHeader>
            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InsightsIcon sx={{ color: 'rgba(25, 118, 210, 0.7)', mr: 1 }} />
              <mui.Typography variant="subtitle1" fontWeight={500}>
                Risk Assessment
              </mui.Typography>
            </mui.Box>
          </SectionHeader>
          <SectionContent>
            <mui.Box sx={{ mb: 3 }}>
              <mui.Typography variant="body2" fontWeight={500} sx={{ mb: 2 }}>
                Risk Rating
              </mui.Typography>
              
              <Select
                options={riskLevelOption}
                value={riskLevelOption.find(option => option.value === riskRating.RATING) || null}
                onChange={handleRatingChange}
                placeholder="Select Risk Rating"
                styles={selectStyles}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: 'rgba(25, 118, 210, 0.8)',
                    primary75: 'rgba(25, 118, 210, 0.6)',
                    primary50: 'rgba(25, 118, 210, 0.4)',
                    primary25: 'rgba(25, 118, 210, 0.2)',
                  },
                })}
                isSearchable={false}
                formatOptionLabel={(option) => (
                  <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <RiskBadge level={option.value} label={option.value} size="small" />
                  </mui.Box>
                )}
              />
            </mui.Box>
            
            <mui.Box>
              <mui.Typography variant="body2" fontWeight={500} sx={{ mb: 2 }}>
                Risk Rationale
              </mui.Typography>
              <mui.TextField
                multiline
                rows={6}
                value={riskRating.RATING_RATIONALE || ''}
                onChange={handleRationaleChange}
                onBlur={handleRationaleBlur}
                placeholder="Provide detailed explanation for the risk rating. Include any relevant context, impact assessment, and control considerations that influenced this rating decision."
                variant="outlined"
                fullWidth
                InputProps={{
                  sx: { 
                    borderRadius: 1,
                    borderColor: 'rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      borderColor: 'rgba(25, 118, 210, 0.5)'
                    },
                    '&.Mui-focused': {
                      borderColor: 'rgba(25, 118, 210, 0.7)'
                    },
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
                  }
                }}
              />
            </mui.Box>
          </SectionContent>
        </StyledSection>
        
        {/* Control Mapping Section */}
        <StyledSection elevation={0}>
          <SectionHeader>
            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SecurityIcon sx={{ color: 'rgba(25, 118, 210, 0.7)', mr: 1 }} />
              <mui.Typography variant="subtitle1" fontWeight={500}>
                Control Mapping
              </mui.Typography>
            </mui.Box>
            <mui.Chip 
              label={`${mappedControls.length} mapped controls`}
              size="small"
              variant={mappedControls.length > 0 ? "filled" : "outlined"}
              sx={{ 
                borderColor: 'rgba(25, 118, 210, 0.3)',
                ...(mappedControls.length > 0 && {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  color: 'rgba(25, 118, 210, 0.9)'
                })
              }}
            />
          </SectionHeader>
          <SectionContent>
            {/* Search box for controls */}
            <SearchBox>
              <SearchIcon sx={{ color: 'rgba(25, 118, 210, 0.7)', mr: 1 }} />
              <mui.InputBase
                placeholder="Search controls..."
                fullWidth
                value={controlSearchText}
                onChange={(e) => setControlSearchText(e.target.value)}
                sx={{ fontSize: '0.875rem' }}
              />
              {controlSearchText && (
                <mui.IconButton 
                  size="small" 
                  onClick={() => setControlSearchText('')}
                  sx={{ ml: 1 }}
                >
                  <CloseIcon fontSize="small" />
                </mui.IconButton>
              )}
            </SearchBox>
            
            <mui.Grid container spacing={3}>
              <mui.Grid item xs={12} md={6}>
                {/* Mapped Controls */}
                <mui.Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <mui.Typography variant="body2" fontWeight={500}>
                    Mapped Controls
                  </mui.Typography>
                </mui.Box>
                
                <ControlListContainer>
                  {mappedControls.length > 0 ? (
                    <mui.List disablePadding>
                      {filterControls(mappedControls).map((control) => (
                        <ControlItem 
                          key={control.controlID}
                          disablePadding
                          mapped={true}
                          secondaryAction={
                            <mui.Tooltip title="Unmap control" arrow placement="left">
                              <div> {/* Wrapper div to handle disabled state */}
                                <mui.IconButton 
                                  edge="end" 
                                  onClick={() => handleUnmapControl(control)}
                                  size="small"
                                  disabled={mappingControl === (control.controlID || control.id)}
                                  sx={{ 
                                    color: 'rgba(0, 0, 0, 0.54)',
                                    '&.Mui-disabled': {
                                      color: 'rgba(0, 0, 0, 0.26)',
                                    }
                                  }}
                                >
                                  {mappingControl === (control.controlID || control.id) ? (
                                    <mui.CircularProgress size={16} thickness={4} />
                                  ) : (
                                    <RemoveCircleIcon fontSize="small" />
                                  )}
                                </mui.IconButton>
                              </div>
                            </mui.Tooltip>
                          }
                        >
                          {renderControl(control, control.controlID)}
                        </ControlItem>
                      ))}
                    </mui.List>
                  ) : (
                    <mui.Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                      <mui.Typography variant="body2">
                        No controls mapped yet
                      </mui.Typography>
                    </mui.Box>
                  )}
                </ControlListContainer>
              </mui.Grid>
              
              <mui.Grid item xs={12} md={6}>
                {/* Available Controls */}
                <mui.Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <mui.Typography variant="body2" fontWeight={500}>
                    Available Controls
                  </mui.Typography>
                  <mui.Typography variant="caption" color="text.secondary">
                    {getUnmappedControls().length} available
                  </mui.Typography>
                </mui.Box>
                
                <ControlListContainer>
                  {filterControls(getUnmappedControls()).length > 0 ? (
                    <mui.List disablePadding>
                      {filterControls(getUnmappedControls()).map((control) => (
                        <ControlItem 
                          key={control.controlID}
                          disablePadding
                          secondaryAction={
                            <mui.Tooltip title="Map control to risk" arrow placement="left">
                              <div> {/* Wrapper div to handle disabled state */}
                                <mui.IconButton 
                                  edge="end" 
                                  onClick={() => handleMapControl(control)}
                                  size="small"
                                  disabled={mappingControl === (control.controlID || control.id)}
                                  sx={{ 
                                    color: 'rgba(0, 0, 0, 0.54)',
                                    '&.Mui-disabled': {
                                      color: 'rgba(0, 0, 0, 0.26)',
                                    }
                                  }}
                                >
                                  {mappingControl === (control.controlID || control.id) ? (
                                    <mui.CircularProgress size={16} thickness={4} color="primary" />
                                  ) : (
                                    <AddCircleRoundedIcon fontSize="small" />
                                  )}
                                </mui.IconButton>
                              </div>
                            </mui.Tooltip>
                          }
                        >
                          {renderControl(control, control.controlID)}
                        </ControlItem>
                      ))}
                    </mui.List>
                  ) : (
                    <mui.Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                      <mui.Typography variant="body2">
                        {controlSearchText ? 'No controls match your search' : 'All controls have been mapped'}
                      </mui.Typography>
                    </mui.Box>
                  )}
                </ControlListContainer>
              </mui.Grid>
            </mui.Grid>
          </SectionContent>
        </StyledSection>
      </mui.DialogContent>
      
      {/* Dialog Actions */}
      <mui.DialogActions sx={{ 
        p: 2, 
        borderTop: '1px solid', 
        borderColor: 'divider',
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'background.paper',
        zIndex: 1
      }}>
        <mui.Button 
          onClick={onClose} 
          variant="outlined" 
          sx={{ 
            borderRadius: 1,
            borderColor: 'rgba(25, 118, 210, 0.3)',
            color: 'rgba(25, 118, 210, 0.8)'
          }}
          disabled={loading}
        >
          Close
        </mui.Button>
      </mui.DialogActions>
    </mui.Dialog>
  );
};

export default ConsolidatedRiskDialog; 